import DynamicDialog from '@/components/DynamicDialog';
import useFormSchema from '@/hooks/useFormSchema';
import { useFetchOrganisations } from '@/http/queries';
import Grid from '@mui/material/Grid2';
import { IconUsersGroup } from '@tabler/icons-react';
import { TextField } from 'mui-rff';
import { Field, Form } from 'react-final-form';
import * as yup from 'yup';
import { noLeadingOrTrailingSpaces } from '@/helpers/yupValidation';
import { useMemo } from 'react';
import validateDomain from '@/pages/admin/validateDomain';
import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { useAtom } from 'jotai';
import { userAtom } from '@/global-store';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';
import { T } from '@/i18n/T';

export default function OrgDialog({
  orgData,
  usePost,
  onSuccess,
  onFail,
  initialValues,
  wrapInForm = true,
  ...dialogProps
}) {
  const { t } = useInlineTranslation();
  const [user] = useAtom(userAtom);
  const { data: orgList } = useFetchOrganisations('all', !!orgData);
  const { mutateAsync: postOrgAsync } = usePost();
  const isAdminLike = user.isAdmin || user.isSalesAdmin;

  const { orgNames, orgDomains } = useMemo(() => {
    return (orgList ?? []).reduce(
      (acc, org) => {
        acc.orgNames.push(org.name);
        acc.orgDomains.push(org.domain);
        return acc;
      },
      { orgNames: [], orgDomains: [] },
    );
  }, [orgList]);

  const { validate, required } = useFormSchema(
    yup.object({
      name: noLeadingOrTrailingSpaces(t('organisationName'))
        .notOneOf(orgNames, t('organisationExist'))
        .max(200)
        .required(),
      domain: noLeadingOrTrailingSpaces(t('domain'))
        .test('valid-domain', function (value) {
          const raw = (value ?? '').trim();
          if (!raw) return true; // .required() behandelt "leer"

          const domain = raw.split(/[\s,]+/).filter(Boolean)[0] ?? '';
          const { isError, textError } = validateDomain(domain);

          return isError ? this.createError({ message: textError }) : true;
        })
        .notOneOf(orgDomains, t('domainExist'))
        .required(),
    }),
  );

  async function onSubmit(values, form) {
    try {
      const response = await postOrgAsync({
        name: values.name.trim(),
        domain: values.domain.trim(),
        selfManaged: isAdminLike ? values.selfManaged : false,
      });

      if (onSuccess) {
        onSuccess(response);
      }

      form.restart();
      return true;
    } catch (error) {
      if (onFail) {
        onFail(error);
      }
      return false;
    }
  }

  return (
    <Form
      validate={validate}
      onSubmit={onSubmit}
      initialValues={{ selfManaged: false, ...initialValues }}
      render={({ handleSubmit }) => {
        const dialog = (
          <DynamicDialog
            {...dialogProps}
            title={<T k='addOrganisation' />}
            confirmBtnText={t('save')}
            activatorText={t('addOrganisation')}
            activatorProps={{
              color: 'primary',
              variant: 'contained',
              startIcon: <IconUsersGroup />,
              ...dialogProps?.activatorProps,
            }}
            onConfirm={handleSubmit}
          >
            <Grid
              container
              sx={{
                alignItems: 'center',
                gap: 2.5,
                justifyContent: 'center',
                my: 2.5,
                px: { xs: 3, md: 4, xl: 5.5 },
              }}
            >
              <TextField
                name='name'
                label={<T k='organisationName' />}
                required={required.name}
                autoFocus
                autoComplete='new-password'
                fullWidth
              />
              <TextField
                name='domain'
                label={<T k='domain' />}
                placeholder={t('selectDomain')}
                helperText={<T k='domainHelperText' />}
                required={required.domain}
                autoComplete='off'
                fullWidth
              />
              {isAdminLike && (
                <Field name='selfManaged'>
                  {({ input, meta }) => (
                    <FormControl fullWidth error={meta.touched && !!meta.error}>
                      <InputLabel id='selfManaged-label' shrink>
                        {t('partnerSelfManageCheck')}
                      </InputLabel>

                      <Select
                        labelId='selfManaged-label'
                        label={t('partnerSelfManageCheck')}
                        value={input.value ?? false}
                        onChange={(e) => input.onChange(e.target.value)}
                        notched
                      >
                        <MenuItem value={true}>true</MenuItem>
                        <MenuItem value={false}>false</MenuItem>
                      </Select>

                      <FormHelperText>
                        {meta.touched && meta.error
                          ? meta.error
                          : t('partnerSelfManageCheckTooltip')}
                      </FormHelperText>
                    </FormControl>
                  )}
                </Field>
              )}
            </Grid>
          </DynamicDialog>
        );

        if (!wrapInForm) {
          return dialog;
        }

        return (
          <form noValidate onSubmit={handleSubmit}>
            {dialog}
          </form>
        );
      }}
    />
  );
}
